<!-- The Rates Preview is the ONLY section expanded by default on first
     open of the RateConfig UI — it's what the user comes to look at;
     scope settings and factor-kind sections collapse out of the way
     until deliberately opened. -->
<mat-expansion-panel class="rpm" [expanded]="true">

  <mat-expansion-panel-header>
    <mat-panel-title>
      <mat-icon>preview</mat-icon>
      <span i18n="@@Rates Preview">Rates Preview</span>
      <span class="rpm-count" *ngIf="rows.length > 0">({{localizeNumber(rows.length)}} {{rows.length === 1 ? 'row' : 'rows'}})</span>
    </mat-panel-title>
    <mat-panel-description>
      <span class="rpm-desc" i18n="@@Rates Preview description">
        Concrete amounts computed from the current factors for every enumerated combination of relevant characteristics.
      </span>
    </mat-panel-description>
  </mat-expansion-panel-header>

  <div class="rpm-body">

    <!-- Preview controls (page counts, facility peer picker, companion toggle).
         All three live in the Rates Preview body together because they ALL
         affect what the preview renders — keeping them physically adjacent
         makes the cause/effect relationship obvious. -->
    <div class="rpm-controls" *ngIf="pagesIsRelevant || rows.length === 0 || availableFacilities.length > 0 || hasCompanion">

      <mat-form-field appearance="outline" class="rpm-page-counts-field"
                      *ngIf="pagesIsRelevant || rows.length === 0">
        <mat-label i18n="@@Preview page counts">Preview page counts</mat-label>
        <input matInput [value]="pageCountsString" [disabled]="readOnly"
               (input)="onPageCountsInput($any($event.target).value)"/>
        <mat-hint i18n="@@Comma-separated. Used when factors depend on pages.">Comma-separated. Used when factors depend on pages.</mat-hint>
      </mat-form-field>

      <mat-form-field appearance="outline" class="rpm-peer-picker-field"
                      *ngIf="availableFacilities.length > 0">
        <mat-label i18n="@@Compare with facility rates">Compare with facility rates</mat-label>
        <mat-select multiple [value]="selectedFacilityIds"
                    (selectionChange)="onSelectedFacilitiesChange($event.value)">
          <mat-option *ngFor="let f of availableFacilities" [value]="f.id">{{f.label}}</mat-option>
        </mat-select>
        <mat-hint i18n="@@Compare facilities hint">
          Pick one or more facilities to surface their rate configs side-by-side in the Rates Preview.
        </mat-hint>
      </mat-form-field>

      <!-- Toggle for the companion (e.g. SP charged-by-network) comparison
           line. The companion is supplied by the host as a separate
           document (`companionDoc`); when on, it appears as the first
           peer line in every cell using the same format as facility peers. -->
      <mat-checkbox class="rpm-companion-toggle"
                    *ngIf="hasCompanion"
                    [checked]="showCompanionAsPeer"
                    (change)="onCompanionToggle($event.checked)">
        <span i18n="@@Show companion peer prefix">Show </span>
        <span class="rpm-companion-toggle-label">{{effectiveCompanionLabel}}</span>
      </mat-checkbox>

      <!-- Hide rows that have no feasible cells. When a scope has many
           enum combinations, most cells can come out infeasible (∞) for
           combos the factors refuse — this toggle lets the user collapse
           the table down to just rows with at least one priced cell. -->
      <mat-checkbox class="rpm-hide-empty-toggle"
                    *ngIf="rows.length > 0"
                    [checked]="hideEmptyRows"
                    (change)="onHideEmptyRowsToggle($event.checked)"
                    i18n-matTooltip="@@Hide rows where every cell is infeasible (∞)."
                    matTooltip="Hide rows where every cell is infeasible (∞).">
        <span i18n="@@Hide ∞-only rows">Hide ∞-only rows</span>
      </mat-checkbox>

    </div>

    <!-- Packing preview — order-level evaluation path.
         Auto-shows only when the active scope has a PER_PACKAGE factor
         or any group conditioned on `carrierServiceLevel` (i.e. when
         there's something the per-book matrix below can't surface).
         The user enters a comma-separated packageTypes[] (PackageType
         IDs), picks a carrier service level, and an optional book
         quantity; the order is priced in one call via
         RateConfigEvaluator.evaluateOrder. -->
    <div class="rpm-packing-preview" *ngIf="packingPreviewRelevant">
      <h4 class="rpm-packing-preview-title">
        <mat-icon>inventory_2</mat-icon>
        <span i18n="@@Packing preview">Packing preview</span>
      </h4>
      <p class="rpm-packing-preview-desc" i18n="@@Packing preview desc">
        Prices the whole order in one call. Enter the package types in the order, the carrier service level, and the book quantity.
      </p>

      <div class="rpm-packing-preview-controls">
        <mat-form-field appearance="outline" class="rpm-packing-preview-package-types-field"
                        *ngIf="packingPreviewShowPackageTypes">
          <mat-label i18n="@@Package types (comma-separated)">Package types (comma-separated)</mat-label>
          <input matInput [value]="packagePreviewPackageTypesRaw"
                 (input)="onPackagePreviewPackageTypesInput($any($event.target).value)"
                 placeholder="e.g. 5, 5, 5, 5, 11"/>
          <mat-hint i18n="@@Package types hint">
            Comma-separated PackageType IDs, one entry per physical package.
          </mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" class="rpm-packing-preview-carrier-field"
                        *ngIf="packingPreviewShowCarrier">
          <mat-label i18n="@@Carrier service level">Carrier service level</mat-label>
          <mat-select [value]="packagePreviewCarrierServiceLevel"
                      (selectionChange)="onPackagePreviewCarrierChange($event.value)">
            <mat-option [value]="''" i18n="@@(none)">(none)</mat-option>
            <mat-option *ngFor="let c of carrierServiceLevels" [value]="c.ID">
              {{c.carrier}} — {{c.serviceLevel}}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="rpm-packing-preview-quantity-field"
                        *ngIf="packingPreviewShowQuantity">
          <mat-label i18n="@@Book quantity">Book quantity</mat-label>
          <input matInput type="number" min="1" step="1"
                 [value]="packagePreviewQuantity"
                 (input)="onPackagePreviewQuantityInput($any($event.target).value)"/>
        </mat-form-field>

        <mat-form-field appearance="outline" class="rpm-packing-preview-facility-field"
                        *ngIf="packingPreviewShowFacility">
          <mat-label i18n="@@Facility">Facility</mat-label>
          <mat-select [value]="packagePreviewFacility"
                      (selectionChange)="onPackagePreviewFacilityChange($event.value)">
            <mat-option [value]="''" i18n="@@(none)">(none)</mat-option>
            <mat-option *ngFor="let f of activeFacilityOptions" [value]="f.id">
              {{f.label}}
            </mat-option>
          </mat-select>
          <mat-hint i18n="@@Producing facility hint">
            The facility producing the order — applied to every factor that groups on facility.
          </mat-hint>
        </mat-form-field>
      </div>

      <div class="rpm-packing-preview-parse-warning" *ngIf="packagePreviewParseWarning">
        <mat-icon>warning_amber</mat-icon>
        <span>{{packagePreviewParseWarning}}</span>
      </div>

      <div class="rpm-packing-preview-result" *ngIf="packagePreviewResult as r">
        <div class="rpm-packing-preview-result-summary" *ngIf="packingPreviewShowPackageTypes">
          <span class="rpm-ppr-label" i18n="@@Packages">Packages</span>
          <span class="rpm-ppr-value">{{packagePreviewPackageTypes.length}}<span class="rpm-ppr-detail" *ngIf="packagePreviewPackageTypes.length > 0"> ({{packagePreviewPackageTypes.join(', ')}})</span></span>
        </div>
        <div class="rpm-packing-preview-result-summary" *ngIf="!r.infeasible && packingPreviewShowQuantity">
          <span class="rpm-ppr-label" i18n="@@Books">Books</span>
          <span class="rpm-ppr-value">{{localizeNumber(packagePreviewQuantity)}}</span>
        </div>
        <!-- Per-book line — shows the per-book unit cost AND the
             quantity multiplier inline so the user can see how the
             entered book quantity drives the per-book contribution to
             the total. When the quantity field isn't relevant (the
             scope has no per-book scaling factor) the multiplier
             collapses to just the unit cost. -->
        <div class="rpm-packing-preview-result-summary" *ngIf="!r.infeasible && packingPreviewShowQuantity">
          <span class="rpm-ppr-label" i18n="@@Per-book">Per-book</span>
          <span class="rpm-ppr-value">
            {{currency}} {{formatPreviewMoney(r.unitCost)}}
            <span class="rpm-ppr-detail">
              ×&nbsp;{{localizeNumber(packagePreviewQuantity)}}&nbsp;<span i18n="@@books">books</span>
              =&nbsp;{{currency}}&nbsp;{{formatPreviewMoney(r.unitCost * packagePreviewQuantity)}}
            </span>
          </span>
        </div>
        <div class="rpm-packing-preview-result-summary" *ngIf="!r.infeasible">
          <span class="rpm-ppr-label" i18n="@@Per-order">Per-order</span>
          <span class="rpm-ppr-value">{{currency}} {{formatPreviewMoney(r.perOrderCost)}}</span>
        </div>
        <div class="rpm-packing-preview-result-summary rpm-packing-preview-result-total" *ngIf="!r.infeasible">
          <span class="rpm-ppr-label" i18n="grand total|The final amount including shipping, handling and tax. Must be distinguishable from Subtotal.@@Total">Total</span>
          <span class="rpm-ppr-value">{{currency}} {{formatPreviewMoney(r.totalCost)}}</span>
        </div>
        <div class="rpm-packing-preview-result-infeasible" *ngIf="r.infeasible">
          <mat-icon>block</mat-icon>
          <span i18n="@@Infeasible — at least one factor refused this combination.">Infeasible — at least one factor refused this combination.</span>
          <span class="rpm-ppr-infeasible-reason" *ngIf="r.infeasibleReason" i18n="@@Infeasible reason detail">
            (factor {{r.infeasibleReason.factorId}} on [{{r.infeasibleReason.characteristics.join(', ')}}] at "{{r.infeasibleReason.comboKey}}")
          </span>
        </div>
      </div>

      <!-- Per-FactorKind breakdown of the packing preview's totals.
           Same grouping helper as the per-cell breakdown in the rate
           table below, so the explanation reads identically across
           the two evaluation paths. Collapsed by default; the user
           toggles it open with the "Show calculation" button. -->
      <div class="rpm-packing-preview-breakdown" *ngIf="packagePreviewBreakdownGroups.length > 0">
        <button mat-stroked-button class="rpm-packing-preview-breakdown-toggle"
                (click)="packagePreviewBreakdownExpanded = !packagePreviewBreakdownExpanded">
          <mat-icon>{{packagePreviewBreakdownExpanded ? 'expand_less' : 'expand_more'}}</mat-icon>
          <span *ngIf="!packagePreviewBreakdownExpanded" i18n="@@Show calculation">Show calculation</span>
          <span *ngIf="packagePreviewBreakdownExpanded" i18n="@@Hide calculation">Hide calculation</span>
        </button>
        <div class="rpm-breakdown rpm-breakdown-inline" *ngIf="packagePreviewBreakdownExpanded">
          <div *ngFor="let g of packagePreviewBreakdownGroups" class="rpm-breakdown-group"
               [class.rpm-breakdown-group-finalize]="g.kind === 'finalize'">
            <div class="rpm-breakdown-group-head">
              <span class="rpm-breakdown-group-label">{{g.label}}</span>
              <span class="rpm-spacer"></span>
              <span class="rpm-breakdown-group-subtotal" *ngIf="g.subtotalTarget !== 'finalize'">
                <span class="rpm-breakdown-group-subtotal-label" *ngIf="g.subtotalTarget === 'unit'"
                      i18n="@@subtotal (unit)">subtotal (unit)</span>
                <span class="rpm-breakdown-group-subtotal-label" *ngIf="g.subtotalTarget === 'perOrder'"
                      i18n="@@subtotal (per order)">subtotal (per order)</span>
                <span class="rpm-breakdown-group-subtotal-value">{{breakdownGroupSubtotalLabel(g)}}</span>
              </span>
            </div>
            <!-- Per-factor blocks for factor-kind buckets. Each block
                 is one RateFactor folded into a single inline math
                 expression: per-group amounts summed with `+`, an
                 optional `× pages` multiplier for PER_NUMERIC_PROPERTY,
                 and a `→ +X.XX` tail showing the contribution. The
                 user reads each block top-down without having to
                 mentally splice multiple step lines together. -->
            <ul class="rpm-breakdown-factors" *ngIf="g.factors.length > 0">
              <li *ngFor="let f of g.factors" class="rpm-breakdown-factor-block">
                <ng-container *ngTemplateOutlet="factorBlockBody; context: {$implicit: f}"></ng-container>
              </li>
            </ul>
            <!-- Finalize bucket — raw steps (global minimums, percent
                 discount, infeasibility short-circuit) describe net
                 adjustments to the already-summed running totals. -->
            <ul class="rpm-breakdown-finalize-steps" *ngIf="g.finalizeSteps.length > 0">
              <li *ngFor="let step of g.finalizeSteps" class="rpm-breakdown-finalize-step">
                <div class="rpm-breakdown-step-note">{{step.note}}</div>
              </li>
            </ul>
          </div>
          <!-- Order totals — final reconciliation line. Shows the
               unit cost × entered quantity + per-order surcharges =
               grand total so the user can confirm that the per-kind
               subtotals (plus any finalize adjustments) reconcile
               against the result displayed in the result summary
               above. Mirrors the per-cell breakdown's totals row. -->
          <div class="rpm-breakdown-totals" *ngIf="packagePreviewResult as r">
            <span class="rpm-breakdown-totals-label" i18n="@@Order total">Order total</span>
            <span class="rpm-breakdown-totals-detail" *ngIf="!r.infeasible">
              {{currency}}&nbsp;{{formatBreakdownAmount(r.unitCost)}}<span i18n="@@ per book"> / book</span>
              <span>
                &nbsp;×&nbsp;{{localizeNumber(packagePreviewQuantity)}}&nbsp;<span i18n="@@books">books</span>
                &nbsp;=&nbsp;{{currency}}&nbsp;{{formatBreakdownAmount(r.unitCost * packagePreviewQuantity)}}
              </span>
              <span *ngIf="r.perOrderCost !== 0">
                &nbsp;+&nbsp;{{currency}}&nbsp;{{formatBreakdownAmount(r.perOrderCost)}}<span i18n="@@ per order"> / order</span>
              </span>
              &nbsp;<span class="rpm-breakdown-totals-grand">
                =&nbsp;{{currency}}&nbsp;{{formatBreakdownAmount(r.totalCost)}}
              </span>
            </span>
            <span class="rpm-breakdown-totals-detail rpm-breakdown-totals-infeasible" *ngIf="r.infeasible" i18n="@@Order infeasible detail">
              Order infeasible — at least one factor refused this combination.
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Inline template for one BreakdownFactorPart. Rendered as a
         span that fits inside the per-factor expression line.
         Defined as a sibling of the packing-preview div so it's
         visible to both the packing-preview breakdown (above) and
         the per-cell breakdown sub-row (below).

         When `p.subParts` is non-empty (PER_PACKAGE multi-group
         iterations), the inner sum `(g1 + g2 + …) = packageAmount`
         renders BEFORE the package label so the math reads
         `( [g1=…] amt1 + [g2=…] amt2 ) = pkgSum  Package N of M:
         packageType=X`. Single-part PER_PACKAGE iterations and
         every non-PER_PACKAGE part skip the inner expansion. -->
    <ng-template #ppPartInline let-p>
      <span class="rpm-breakdown-part">
        <ng-container *ngIf="p.subParts && p.subParts.length > 0; else ppPartBareAmount">
          <!-- Multi-group PER_PACKAGE iteration — inner sum first. -->
          <span class="rpm-breakdown-paren">(</span>
          <ng-container *ngFor="let sp of p.subParts; let spi = index">
            <span class="rpm-breakdown-plus" *ngIf="spi > 0">+</span>
            <ng-container *ngTemplateOutlet="ppPartInline; context: {$implicit: sp}"></ng-container>
          </ng-container>
          <span class="rpm-breakdown-paren">)</span>
          <span class="rpm-breakdown-eq">=&nbsp;{{formatBreakdownAmount(p.amount)}}</span>
        </ng-container>
        <ng-template #ppPartBareAmount>
          <span class="rpm-breakdown-amount">{{formatBreakdownAmount(p.amount)}}</span>
          <span class="rpm-breakdown-key" *ngIf="p.characteristics && p.characteristics.length > 0">
            [{{p.characteristics.join('|')}}&nbsp;=&nbsp;{{p.matchedKey}}]
            <span class="rpm-breakdown-inherited" *ngIf="p.inheritedFromKey">
              <span i18n="@@inherited from">inherited from</span>&nbsp;{{p.inheritedFromKey}}
            </span>
          </span>
        </ng-template>
        <span class="rpm-breakdown-pp-note" *ngIf="p.perPackageNote">{{p.perPackageNote}}</span>
      </span>
    </ng-template>

    <!-- Shared per-factor block body. Rendered once per BreakdownFactor
         by both the packing-preview "Show calculation" panel and the
         per-cell breakdown sub-row in the rate table. Factored out so
         the two locations stay in sync — adding a new layout branch
         (e.g. the PER_PACKAGE vertical layout) only needs to happen
         here, not in two places.

         Two layout modes:
          - Default: single horizontal expression
            `[id] (p1 + p2 + …) = sum  × mul (label) = raw  → bumped …
             → +contribution unit-cost`
            Used for every factor kind except multi-package PER_PACKAGE.
          - PER_PACKAGE vertical: each package iteration on its own
            indented line so two-group × three-package factors don't
            spill into a 200-char wall of math. The roll-up `→
            +contribution per-order cost` sits at the bottom. -->
    <ng-template #factorBlockBody let-f>
      <ng-container *ngIf="factorRendersPerPackageVertically(f); else factorBlockHorizontal">
        <!-- PER_PACKAGE vertical layout. -->
        <div class="rpm-breakdown-factor-line rpm-breakdown-factor-line-header">
          <span class="rpm-breakdown-factor-id" *ngIf="f.factorId">#{{f.factorId}}</span>
          <span class="rpm-spacer"></span>
          <!-- Final contribution + which side it added to. Anchored
               to the header line so the user sees the total before
               the per-package detail (the usual "answer first, work
               below" pattern for big computations). -->
          <span class="rpm-breakdown-min" *ngIf="factorMinBumped(f)">
            →&nbsp;<span i18n="@@bumped by min">bumped by min</span>&nbsp;{{formatBreakdownAmount(f.perItemMinimum)}}
          </span>
          <span class="rpm-breakdown-contrib">
            →&nbsp;{{factorContributionLabel(f)}}
            <span *ngIf="f.targetTotal === 'unit'" i18n="@@unit cost">unit cost</span>
            <span *ngIf="f.targetTotal === 'perOrder'" i18n="@@per-order cost">per-order cost</span>
          </span>
        </div>
        <ul class="rpm-breakdown-package-list">
          <li *ngFor="let p of f.parts" class="rpm-breakdown-package-item">
            <div class="rpm-breakdown-package-label" *ngIf="p.perPackageNote">{{p.perPackageNote}}</div>
            <div class="rpm-breakdown-factor-line rpm-breakdown-package-expr">
              <ng-container *ngIf="p.subParts && p.subParts.length > 0; else packageBare">
                <!-- Multi-group package: render the inner sum
                     `( g1 + g2 + … ) = packageAmount`. Each sub-part
                     uses the existing #ppPartInline template (which
                     handles single-group rendering with the
                     `[chars=key]` annotation). -->
                <span class="rpm-breakdown-expr">
                  <span class="rpm-breakdown-paren">(</span>
                  <ng-container *ngFor="let sp of p.subParts; let spi = index">
                    <span class="rpm-breakdown-plus" *ngIf="spi > 0">+</span>
                    <ng-container *ngTemplateOutlet="ppPartInline; context: {$implicit: sp}"></ng-container>
                  </ng-container>
                  <span class="rpm-breakdown-paren">)</span>
                  <span class="rpm-breakdown-eq">=&nbsp;{{formatBreakdownAmount(p.amount)}}</span>
                </span>
              </ng-container>
              <ng-template #packageBare>
                <!-- Single-group package: render just the matched
                     amount + `[chars=key]` annotation. -->
                <span class="rpm-breakdown-expr">
                  <span class="rpm-breakdown-amount">{{formatBreakdownAmount(p.amount)}}</span>
                  <span class="rpm-breakdown-key" *ngIf="p.characteristics && p.characteristics.length > 0">
                    [{{p.characteristics.join('|')}}&nbsp;=&nbsp;{{p.matchedKey}}]
                    <span class="rpm-breakdown-inherited" *ngIf="p.inheritedFromKey">
                      <span i18n="@@inherited from">inherited from</span>&nbsp;{{p.inheritedFromKey}}
                    </span>
                  </span>
                </span>
              </ng-template>
            </div>
          </li>
        </ul>
        <div class="rpm-breakdown-factor-note" *ngIf="f.note">{{f.note}}</div>
      </ng-container>

      <!-- Default horizontal layout for every non-PER_PACKAGE-vertical
           factor (PER_ORDER / PER_ITEM / PER_QUANTITY /
           PER_NUMERIC_PROPERTY, and PER_PACKAGE factors with no
           per-package iterations such as the per-cell-matrix
           placeholder or the empty-packageTypes case). -->
      <ng-template #factorBlockHorizontal>
        <div class="rpm-breakdown-factor-line">
          <span class="rpm-breakdown-factor-id" *ngIf="f.factorId">#{{f.factorId}}</span>
          <!-- baseAmount factor (no groups). -->
          <span class="rpm-breakdown-expr" *ngIf="f.baseAmountPart">
            <span class="rpm-breakdown-amount">{{partAmountLabel(f.baseAmountPart)}}</span>
          </span>
          <!-- Single-part factor — render the part inline with no
               outer parentheses or trailing `=`. -->
          <span class="rpm-breakdown-expr" *ngIf="!f.baseAmountPart && !factorHasMultipleParts(f) && f.parts.length === 1">
            <ng-container *ngTemplateOutlet="ppPartInline; context: {$implicit: f.parts[0]}"></ng-container>
          </span>
          <!-- Multi-part factor — render parts joined by `+`, wrap
               in `(…)`, then show `= groupSum`. -->
          <span class="rpm-breakdown-expr" *ngIf="!f.baseAmountPart && factorHasMultipleParts(f)">
            <span class="rpm-breakdown-paren">(</span>
            <ng-container *ngFor="let p of f.parts; let pi = index">
              <span class="rpm-breakdown-plus" *ngIf="pi > 0">+</span>
              <ng-container *ngTemplateOutlet="ppPartInline; context: {$implicit: p}"></ng-container>
            </ng-container>
            <span class="rpm-breakdown-paren">)</span>
            <span class="rpm-breakdown-eq">=&nbsp;{{factorGroupSumLabel(f)}}</span>
          </span>
          <!-- × multiplier (PER_NUMERIC_PROPERTY only). -->
          <span class="rpm-breakdown-mul" *ngIf="f.multiplier !== undefined">
            ×&nbsp;{{factorMultiplierLabel(f)}}
            <span class="rpm-breakdown-mul-label" *ngIf="f.multiplierLabel">({{f.multiplierLabel}})</span>
            <span class="rpm-breakdown-eq">=&nbsp;{{factorRawContributionLabel(f)}}</span>
          </span>
          <!-- Per-factor minimum bumped the result. -->
          <span class="rpm-breakdown-min" *ngIf="factorMinBumped(f)">
            →&nbsp;<span i18n="@@bumped by min">bumped by min</span>&nbsp;{{formatBreakdownAmount(f.perItemMinimum)}}
          </span>
          <!-- Final contribution + which side it added to. -->
          <span class="rpm-breakdown-contrib">
            →&nbsp;{{factorContributionLabel(f)}}
            <span *ngIf="f.targetTotal === 'unit'" i18n="@@unit cost">unit cost</span>
            <span *ngIf="f.targetTotal === 'perOrder'" i18n="@@per-order cost">per-order cost</span>
          </span>
        </div>
        <div class="rpm-breakdown-factor-note" *ngIf="f.note">{{f.note}}</div>
      </ng-template>
    </ng-template>

    <div *ngIf="rows.length === 0" class="rpm-empty">
      <mat-icon>info_outline</mat-icon>
      <span>{{emptyNote || ('Nothing to demonstrate yet.' )}}</span>
    </div>

    <div class="rpm-table-scroll" *ngIf="rows.length > 0">
      <table class="rpm-table">
        <thead>
          <!-- Header row 1: axis columns + (optional) Pages super-row
               spanning the tier columns underneath each page count.
               When pages aren't relevant `pageColumns` collapses to a
               single empty-labelled entry, the per-tier `<th>`s below
               render their own labels directly, and this row gets only
               the axis columns (no Pages span) — matches the legacy
               single-header-row layout. -->
          <tr>
            <th *ngFor="let opts of columnFilterOptions" class="rpm-head-axis"
                [attr.rowspan]="pagesIsRelevant ? 2 : null">
              <div class="rpm-head-axis-label">{{columnLabel(opts.property)}}</div>
              <!-- Per-column multi-select filter. Empty selection = no
                   filter on this column (all values pass). Options are
                   only the values actually present in the generated
                   rows, so the dropdown never offers an empty match.
                   Single-value columns hide the filter entirely. -->
              <mat-form-field appearance="outline" class="rpm-head-filter-field"
                              *ngIf="opts.values.length > 1">
                <mat-select multiple
                            [value]="columnFilters[opts.property] || []"
                            (selectionChange)="onColumnFilterChange(opts.property, $event.value)"
                            i18n-placeholder="@@All"
                            placeholder="All">
                  <mat-option *ngFor="let v of opts.values" [value]="v.raw">{{v.display}}</mat-option>
                </mat-select>
              </mat-form-field>
            </th>
            <th *ngIf="numberUpIsRelevant" class="rpm-head-axis"
                [attr.rowspan]="pagesIsRelevant ? 2 : null"
                i18n="@@Number up">Number up</th>
            <!-- When pages are an axis dimension we render an OUTER
                 header row of page-count labels, each spanning the tier
                 columns repeated underneath. When pages aren't an axis,
                 we skip this group-spanning row entirely and let row 2
                 below render the tier columns directly. -->
            <ng-container *ngIf="pagesIsRelevant">
              <th *ngFor="let pc of pageColumns" class="rpm-head-pages-group"
                  [attr.colspan]="tierColumns.length">
                <span class="rpm-head-pages-label">{{pc.label}}</span>
                <span class="rpm-head-pages-unit" i18n="@@pages">pages</span>
              </th>
            </ng-container>
            <!-- Legacy single-row tier headers when pages aren't an axis. -->
            <ng-container *ngIf="!pagesIsRelevant">
              <th *ngFor="let t of tierColumns" class="rpm-head-tier">
                <span class="rpm-head-tier-qty">{{t.label}}</span>
                <span class="rpm-head-tier-unit">{{currency}} <span i18n="price per single book|Unit-price column header, e.g. &quot;USD / book&quot;.@@/ book">/ book</span></span>
              </th>
            </ng-container>
          </tr>
          <!-- Header row 2: tier labels, repeated under each page-count
               group from row 1. Only rendered when pages are an axis;
               otherwise the legacy single header row above carries the
               tier labels directly. -->
          <tr *ngIf="pagesIsRelevant">
            <ng-container *ngFor="let pc of pageColumns">
              <th *ngFor="let t of tierColumns" class="rpm-head-tier">
                <span class="rpm-head-tier-qty">{{t.label}}</span>
                <span class="rpm-head-tier-unit">{{currency}} <span i18n="price per single book|Unit-price column header, e.g. &quot;USD / book&quot;.@@/ book">/ book</span></span>
              </th>
            </ng-container>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let fr of filteredRows; trackBy: trackFilteredRow">
          <tr>
            <td *ngFor="let l of fr.row.labels" class="rpm-cell-axis"
                [matTooltip]="l.tooltip"
                [matTooltipDisabled]="!l.tooltip"
                matTooltipClass="rpm-tooltip-multiline">{{l.display}}</td>
            <td *ngIf="numberUpIsRelevant" class="rpm-cell-axis rpm-cell-numberup">{{fr.row.numberUpLabel}}</td>
            <!-- Clicking a value cell opens (or toggles closed) a
                 step-by-step breakdown panel inserted as a sub-row
                 immediately under this one — keeping the explanation
                 in the user's line of sight instead of forcing a
                 scroll to a panel below the whole table. -->
            <td *ngFor="let cell of fr.row.cells; let ci = index; trackBy: trackCell"
                class="rpm-cell-amount rpm-cell-clickable"
                [class.rpm-cell-infeasible]="cell.current && cell.current.infeasible"
                [class.rpm-cell-selected]="selectedCell && selectedCell.rowIndex === fr.rowIndex && selectedCell.cellIndex === ci"
                [matTooltip]="cell.tooltip"
                [matTooltipDisabled]="!cell.tooltipEnabled"
                (click)="onCellClick(fr.rowIndex, ci)">
              <div class="rpm-cell-current">{{cell.display}}</div>
              <!-- Delta vs. last-saved baseline. Hidden when no baseline exists,
                   when both sides are infeasible, or when the change is exactly
                   zero. Up arrow = price went up (green), down arrow = price went
                   down (red) — matching standard market/financial conventions
                   where green-up means "growth / higher value". -->
              <div class="rpm-cell-delta"
                   *ngIf="cell.delta"
                   [class.rpm-delta-up]="cell.delta.direction === 'up'"
                   [class.rpm-delta-down]="cell.delta.direction === 'down'"
                   [class.rpm-delta-new]="cell.delta.direction === 'new'"
                   [class.rpm-delta-removed]="cell.delta.direction === 'removed'">
                <mat-icon class="rpm-delta-arrow" *ngIf="cell.delta.direction === 'up'">arrow_upward</mat-icon>
                <mat-icon class="rpm-delta-arrow" *ngIf="cell.delta.direction === 'down'">arrow_downward</mat-icon>
                <mat-icon class="rpm-delta-arrow" *ngIf="cell.delta.direction === 'new'">fiber_new</mat-icon>
                <mat-icon class="rpm-delta-arrow" *ngIf="cell.delta.direction === 'removed'">block</mat-icon>
                <span class="rpm-delta-abs">{{cell.delta.absLabel}}</span>
                <span class="rpm-delta-pct" *ngIf="cell.delta.pctLabel">({{cell.delta.pctLabel}})</span>
              </div>
              <!-- Per-peer lines. One per configured peer doc (e.g. each
                   facility selected for comparison against the SP rate),
                   plus the companion-cost doc as the first synthetic peer
                   when the companion toggle is on. Each line shows the
                   peer's evaluated value for the same cell, plus a delta
                   vs. the current rate. Up = peer is pricier, down = peer
                   is cheaper, same = parity. -->
              <div class="rpm-cell-peer-delta"
                   *ngFor="let pd of cell.peerDeltas"
                   [class.rpm-peer-up]="pd.direction === 'up'"
                   [class.rpm-peer-down]="pd.direction === 'down'"
                   [class.rpm-peer-same]="pd.direction === 'same'"
                   [class.rpm-peer-current-infeasible]="pd.direction === 'currentInfeasible'"
                   [class.rpm-peer-peer-infeasible]="pd.direction === 'peerInfeasible'"
                   [class.rpm-peer-both-infeasible]="pd.direction === 'bothInfeasible'"
                   [matTooltip]="pd.label">
                <span class="rpm-peer-label">{{pd.label}}:</span>
                <span class="rpm-peer-value">{{pd.valueLabel}}</span>
                <span class="rpm-peer-delta-abs"
                      *ngIf="pd.direction !== 'same' && pd.direction !== 'bothInfeasible'">
                  ({{pd.absLabel}}<ng-container *ngIf="pd.pctLabel">, {{pd.pctLabel}}</ng-container>)
                </span>
              </div>
            </td>
          </tr>
          <!-- Inline per-cell breakdown sub-row. Inserted DIRECTLY below
               the row whose cell was clicked so the user can see the
               step-by-step computation without scrolling away from the
               cell. Spans the full table width. Toggled off by clicking
               the same cell again or the × button. -->
          <tr *ngIf="selectedCell && selectedCell.rowIndex === fr.rowIndex && selectedBreakdownGroups.length > 0"
              class="rpm-breakdown-row">
            <td [attr.colspan]="totalColumns" class="rpm-breakdown-cell">
              <div class="rpm-breakdown rpm-breakdown-inline">
                <div class="rpm-breakdown-head">
                  <mat-icon>info_outline</mat-icon>
                  <span class="rpm-breakdown-title" i18n="@@Cell breakdown">Cell breakdown</span>
                  <span class="rpm-breakdown-coord">{{selectedCellLabel}}</span>
                  <span class="rpm-spacer"></span>
                  <button mat-icon-button (click)="onCellClick(selectedCell.rowIndex, selectedCell.cellIndex)"
                          i18n-matTooltip="@@Close breakdown" matTooltip="Close breakdown">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
                <!-- Per-FactorKind breakdown sections. Each bucket
                     groups every step from factors of one kind
                     (Per order / Per item / Per book / Per page /
                     Per package) plus a trailing `Finalize` bucket
                     for global-minimum / percentage-discount /
                     infeasibility steps. The bucket header carries
                     the kind label + a per-bucket subtotal so the
                     user can see at a glance how much each kind
                     contributed to the cell's unit / per-order cost. -->
                <div *ngFor="let g of selectedBreakdownGroups" class="rpm-breakdown-group"
                     [class.rpm-breakdown-group-finalize]="g.kind === 'finalize'">
                  <div class="rpm-breakdown-group-head">
                    <span class="rpm-breakdown-group-label">{{g.label}}</span>
                    <span class="rpm-spacer"></span>
                    <span class="rpm-breakdown-group-subtotal" *ngIf="g.subtotalTarget !== 'finalize'">
                      <span class="rpm-breakdown-group-subtotal-label" *ngIf="g.subtotalTarget === 'unit'"
                            i18n="@@subtotal (unit)">subtotal (unit)</span>
                      <span class="rpm-breakdown-group-subtotal-label" *ngIf="g.subtotalTarget === 'perOrder'"
                            i18n="@@subtotal (per order)">subtotal (per order)</span>
                      <span class="rpm-breakdown-group-subtotal-value">{{breakdownGroupSubtotalLabel(g)}}</span>
                    </span>
                  </div>
                  <!-- Per-factor blocks (same shape as the packing-
                       preview breakdown above). One block per factor:
                       per-group amounts joined with `+`, an optional
                       `× pages` multiplier for PER_NUMERIC_PROPERTY,
                       and a `→ +X.XX unit cost` tail. -->
                  <ul class="rpm-breakdown-factors" *ngIf="g.factors.length > 0">
                    <li *ngFor="let f of g.factors" class="rpm-breakdown-factor-block">
                      <ng-container *ngTemplateOutlet="factorBlockBody; context: {$implicit: f}"></ng-container>
                    </li>
                  </ul>
                  <ul class="rpm-breakdown-finalize-steps" *ngIf="g.finalizeSteps.length > 0">
                    <li *ngFor="let step of g.finalizeSteps" class="rpm-breakdown-finalize-step">
                      <div class="rpm-breakdown-step-note">{{step.note}}</div>
                    </li>
                  </ul>
                </div>
                <!-- Cell totals — final reconciliation line below the
                     per-kind groups. Shows the engine's reported
                     unitCost / perOrderCost / totalCost so the user
                     can confirm that the bucket subtotals (plus any
                     finalize adjustments) reconcile against the
                     value displayed in the table cell. -->
                <div class="rpm-breakdown-totals" *ngIf="selectedBreakdownTotals as t">
                  <span class="rpm-breakdown-totals-label" i18n="@@Cell total">Cell total</span>
                  <span class="rpm-breakdown-totals-detail" *ngIf="!t.infeasible">
                    {{currency}}&nbsp;{{formatBreakdownAmount(t.unitCost)}}<span i18n="@@ per book"> / book</span>
                    <span *ngIf="t.perOrderCost !== 0">
                      &nbsp;+&nbsp;{{currency}}&nbsp;{{formatBreakdownAmount(t.perOrderCost)}}<span i18n="@@ per order"> / order</span>
                    </span>
                    &nbsp;<span class="rpm-breakdown-totals-grand">
                      =&nbsp;{{currency}}&nbsp;{{formatBreakdownAmount(t.totalCost)}}<span i18n="@@ at qty"> at qty</span>&nbsp;{{localizeNumber(t.quantity)}}
                    </span>
                  </span>
                  <span class="rpm-breakdown-totals-detail rpm-breakdown-totals-infeasible" *ngIf="t.infeasible" i18n="@@Cell infeasible detail">
                    Cell infeasible — at least one factor refused this combination.
                  </span>
                </div>
              </div>
            </td>
          </tr>
          </ng-container>
        </tbody>
      </table>
    </div>

    <!-- Per-cell breakdown is now rendered INLINE as a sub-row directly
         below the clicked cell's row (see the table body above). That
         keeps the explanation in the user's line of sight instead of
         forcing a scroll to a separate panel below the table. -->

    <div class="rpm-legend" *ngIf="rows.length > 0">
      <span class="rpm-legend-item" i18n="@@Cell shows unit cost legend">
        Cell shows unit cost. Per-order surcharges (if any) appear in parentheses.
      </span>
      <span class="rpm-legend-sep">·</span>
      <span class="rpm-legend-item" *ngIf="hasBaseline">
        <mat-icon class="rpm-delta-arrow rpm-delta-up">arrow_upward</mat-icon>
        <span i18n="@@= price increased vs. last save.">= price increased vs. last save.</span>
        <span class="rpm-legend-sep">·</span>
        <mat-icon class="rpm-delta-arrow rpm-delta-down">arrow_downward</mat-icon>
        <span i18n="@@= price decreased vs. last save.">= price decreased vs. last save.</span>
      </span>
      <span class="rpm-legend-sep" *ngIf="hasBaseline">·</span>
      <span class="rpm-legend-item" *ngIf="hasPeers || (hasCompanion && showCompanionAsPeer)">
        <span class="rpm-peer-label" i18n="@@peer:value">peer:value</span>
        <span i18n="@@Per-peer comparison legend">
          = per-peer comparison. Up = peer pricier; down = peer cheaper.
        </span>
      </span>
      <span class="rpm-legend-sep" *ngIf="hasPeers || (hasCompanion && showCompanionAsPeer)">·</span>
      <span class="rpm-legend-item">
        <span class="rpm-legend-swatch rpm-cell-infeasible">∞</span>
        <span i18n="@@= infeasible (factor refused this combo).">= infeasible (factor refused this combo).</span>
      </span>
    </div>

  </div>
</mat-expansion-panel>

